import { observer } from 'mobx-react';
import { Skeleton } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { InfoOutlineIcon } from '@ring-icons';
import useStores from '$src/hooks/useStores';
import useModalEvents from '$src/hooks/useModalEvents';
import useLocationContext from '$src/hooks/useLocationContext';
import useFeature from '$src/hooks/useFeature';
import PrivateGuardForm from '$src/components/PrivateGuardForm';
import NavigationTitle from '$src/components/NavigationTitle';
import { CustomIcon } from '$src/components/CustomIcon';
import AuthorizeOwnGuardDialog from '$src/components/AuthorizeOwnGuardDialog';
import { ModalCustom } from '$src/components/Modal';
import { Button } from '@amzn/ring-react-components/csr/components/Button';
import { CalloutCard } from '@amzn/ring-react-components/csr/components/CalloutCard';
import Card from '$src/components/Card';

import './PrivateGuard.less';

const PRIVATE_GUARD_STRINGS = {
  PAGE_TITLE: 'Private Guard',
  PAGE_DESCRIPTION:
    'If you have a private security service, our agents can request a dispatch from that service instead of police. This may result in a faster response.',
  PAGE_DESCRIPTION_LEGAL: 'Your contact details will be shared with the private guard you have entered.',
  PENDING_TITLE: 'Your Private Guard request is pending.',
  PENDING_MESSAGE: 'Look for a confirmation email from Ring within 1 business day.',
  FORM_CALLOUT_TITLE: 'To enable a response from your service:',
  FORM_CALLOUT_MESSAGE: 'Verify that they respond to dispatch requests from Ring, remain in good standing with them, and keep this contact information current.',
  ADD_GUARD_INFO_BUTTON: 'Add Guard Info',
  NO_INFO_ADDED: 'No Info Added',
  SAVE_BUTTON: 'Save',
  REMOVE_BUTTON: 'Remove Private Guard',
  REMOVE_CONFIRM_TITLE: 'Confirm',
  REMOVE_CONFIRM_HEADER: 'Remove your Private Guard?',
  REMOVE_CONFIRM_MESSAGE: 'By removing this Private Guard, you’re authorizing agents to request your local police in an emergency, rather than a private security guard service.',
  REMOVE_CONFIRM_BUTTON: 'Remove',
  CANCEL_BUTTON: 'Cancel',
};

function PrivateGuard() {
  const { guardStore, locationContextStore } = useStores();
  const { fetchGuard, loading, guard, isGuardCreationPending, createGuard, deleteGuard } = guardStore;
  const { locationOwnerId } = locationContextStore;
  const locationId = useLocationContext();
  const [toggleModal, showModal] = useModalEvents();
  const [toggleRemoveModal, showRemoveModal] = useModalEvents();
  const [allowSaveForm, setAllowSaveForm] = useState(false);
  const [showGuardForm, setShowGuardForm] = useState(false);
  const formValues = useRef({});
  const [isPrivateGuardDeleteEnabled] = useFeature('stark-feature-next-private-guard-delete');

  useEffect(() => {
    if (locationOwnerId && locationId) {
      fetchGuard(locationOwnerId, locationId);
    }
  }, [fetchGuard, locationOwnerId, locationId]);

  const onHandleAuthorize = () => {
    toggleModal();
    createGuard(locationOwnerId, locationId, formValues.current);
  };

  const onHandleFormChange = (newCompanyName, newPhoneNumber, isFormValid) => {
    formValues.current = {
      agencyName: newCompanyName,
      phone: newPhoneNumber,
    };
    setAllowSaveForm(isFormValid);
  };

  const onHandleRemove = () => {
    deleteGuard(locationOwnerId, locationId);
    toggleRemoveModal();
  };

  const isGuardConfigured = guard?.agencyName && guard?.phone;

  return (
    <div className="private-guard" data-testid="private-guard-page">
      <div className="private-guard__page-content">
        <NavigationTitle
          title={PRIVATE_GUARD_STRINGS.PAGE_TITLE}
          icon={() => <CustomIcon type="regular" name="private_guard.svg" className="navigation-title__icon" />}
        >
          {!showGuardForm && (
            <>
              <p>{PRIVATE_GUARD_STRINGS.PAGE_DESCRIPTION}</p>
              <p style={{ marginTop: '12px' }}>{PRIVATE_GUARD_STRINGS.PAGE_DESCRIPTION_LEGAL}</p>
            </>
          )}
        </NavigationTitle>

        {showGuardForm && !isGuardConfigured && (
          <CalloutCard
            icon={<InfoOutlineIcon width="24px" />}
            className="private-guard__page-content__warning"
            status="default"
            title={PRIVATE_GUARD_STRINGS.FORM_CALLOUT_TITLE}
            data-testid="guard-form-info-callout"
          >
            <p>{PRIVATE_GUARD_STRINGS.FORM_CALLOUT_MESSAGE}</p>
          </CalloutCard>
        )}

        <Skeleton active loading={loading}>
          {isGuardCreationPending ? (
            <CalloutCard
              icon={<InfoOutlineIcon width="24px" />}
              className="private-guard__page-content__warning"
              status="default"
              title={PRIVATE_GUARD_STRINGS.PENDING_TITLE}
              data-testid="pending-private-guard-warning"
            >
              <p>{PRIVATE_GUARD_STRINGS.PENDING_MESSAGE}</p>
            </CalloutCard>
          ) : (
            <>
              {isGuardConfigured && (
                <Card 
                  variant="primary" 
                  isHoverDisabled={true}
                  data-testid="guard-info-card"
                  style={{ marginBottom: '24px' }}
                >
                  <div className="card__guard-info">
                    <div className="card__guard-info-icon">
                      <CustomIcon type="regular" name="name_tag.svg" />
                    </div>
                    <div className="card__guard-info-content">
                      <div className="card__guard-info-header">
                        Guard Info
                      </div>
                      <div className="card__guard-info-company">
                        {guard?.agencyName}
                      </div>
                      <div className="card__guard-info-phone">
                        {guard?.phone}
                      </div>
                    </div>
                  </div>
                </Card>
              )}
              {!isGuardConfigured && showGuardForm && (
                <PrivateGuardForm
                  initialCompanyName={guard?.agencyName}
                  initialPhoneNumber={guard?.phone}
                  onChange={onHandleFormChange}
                  editableForm={true}
                />
              )}
              {!isGuardConfigured && !showGuardForm && (
                <>
                  <div className="private-guard__add-guard-container">
                    <Button 
                      color="primary" 
                      size="m" 
                      variant="filled" 
                      onClick={() => setShowGuardForm(true)}
                      data-testid="add-guard-info-button"
                    >
                      {PRIVATE_GUARD_STRINGS.ADD_GUARD_INFO_BUTTON}
                    </Button>
                  </div>
                  <Card 
                    variant="empty" 
                    isHoverDisabled={true}
                    data-testid="no-guard-info-card"
                    style={{ marginTop: '24px' }}
                  >
                    {PRIVATE_GUARD_STRINGS.NO_INFO_ADDED}
                  </Card>
                </>
              )}
              {isGuardConfigured && isPrivateGuardDeleteEnabled && (
                <Card 
                  variant="error" 
                  isHoverDisabled={true}
                  data-testid="remove-guard-card"
                  style={{ marginTop: '24px' }}
                >
                  <div className="card__remove-guard">
                    <div className="card__remove-guard-left">
                      <div className="card__remove-guard-icon">
                        <CustomIcon type="regular" name="trash.svg" />
                      </div>
                      <div className="card__remove-guard-text">Remove Guard Info</div>
                    </div>
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        toggleRemoveModal();
                      }}
                      className="card__remove-guard-link"
                    >
                      Remove
                    </a>
                  </div>
                </Card>
              )}
            </>
          )}
        </Skeleton>
      </div>
      {!isGuardCreationPending && !loading && !isGuardConfigured && showGuardForm && (
        <div className="private-guard__page-footer">
          <Button color="primary" size="m" variant="filled" disabled={!allowSaveForm} onClick={toggleModal}>
            {PRIVATE_GUARD_STRINGS.SAVE_BUTTON}
          </Button>
        </div>
      )}
      <AuthorizeOwnGuardDialog showModal={showModal} handleAuthorize={onHandleAuthorize} handleCancel={toggleModal} setModalVisible={toggleModal} />
      <ModalCustom
        title={PRIVATE_GUARD_STRINGS.REMOVE_CONFIRM_TITLE}
        contentTitle={
          <>
            <CustomIcon 
              type="regular" 
              name="warning.svg" 
              style={{ 
                transform: 'scale(2)',
                display: 'inline-block',
                marginLeft: '12px',
                marginBottom: '24px',
                color: 'var(--negative-base)'
              }} 
            />
            <h2>{PRIVATE_GUARD_STRINGS.REMOVE_CONFIRM_HEADER}</h2>
          </>
        }
        width="650px"
        contentMessage={PRIVATE_GUARD_STRINGS.REMOVE_CONFIRM_MESSAGE}
        visible={showRemoveModal}
        handleOk={onHandleRemove}
        setModalVisible={toggleRemoveModal}
        okText={PRIVATE_GUARD_STRINGS.REMOVE_CONFIRM_BUTTON}
        cancelText={PRIVATE_GUARD_STRINGS.CANCEL_BUTTON}
        danger={true}
        cancelOnX
        data-testid="remove-private-guard-modal"
      />
    </div>
  );
}

export default observer(PrivateGuard);
